import Hls from "hls.js";
import { computeTargetTime, type FragmentLike } from "./sync-math.js";

/**
 * Keeps this player aligned to an absolute live position shared by every viewer,
 * using the stream's `EXT-X-PROGRAM-DATE-TIME`. hls.js holds the latency smoothly
 * via playback-rate control (`maxLiveSyncPlaybackRate`); this class corrects
 * absolute drift and fast-aligns late joiners with an occasional hard seek — so
 * two tabs that started seconds apart converge to the same frame.
 *
 * If the manifest has no PDT, this is a no-op and hls.js's own latency sync
 * (relative to the shared manifest live edge) still keeps viewers within ~1
 * segment of each other.
 */
const SEEK_THRESHOLD_S = 2; // hard-seek only when off by more than this

export class LiveSync {
  private started = false;

  constructor(
    private readonly hls: Hls,
    private readonly video: HTMLVideoElement,
    private readonly targetLatencySeconds: number,
  ) {}

  start(): void {
    if (this.started) return;
    this.started = true;
    this.hls.on(Hls.Events.LEVEL_UPDATED, this.onLevelUpdated);
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;
    this.hls.off(Hls.Events.LEVEL_UPDATED, this.onLevelUpdated);
  }

  private onLevelUpdated = (_event: unknown, data: { details?: { fragments?: FragmentLike[] } }): void => {
    const cur = this.video.currentTime;
    if (!Number.isFinite(cur) || cur === 0) return; // playback not established yet

    const target = computeTargetTime(data.details?.fragments, this.targetLatencySeconds * 1000);
    if (target == null) return; // no PDT → hls.js latency sync handles it

    if (Math.abs(cur - target) <= SEEK_THRESHOLD_S) return; // small drift → hls.js rate control

    let t = target;
    if (target > cur) {
      // Forward correction (behind live): NEVER seek past what's buffered ahead —
      // `seekable.end` can be well beyond the buffered range, so seeking to it
      // lands in a gap and forces a rebuffer. Cap to the buffered edge; if nothing
      // is buffered ahead, skip and let hls.js load + rate-control catch up.
      const end = bufferedEndAhead(this.video, cur);
      if (end == null) return;
      t = Math.min(target, end - BUFFER_MARGIN_S);
      if (t <= cur + SEEK_THRESHOLD_S) return; // not worth a small forward jump
    } else {
      // Backward correction (ahead of live): clamp to the seekable start, and only
      // seek if the target is actually buffered — an evicted past region would
      // rebuffer just like a forward gap. Otherwise skip and let rate control drift.
      const seekable = this.video.seekable;
      if (seekable.length > 0) t = Math.max(target, seekable.start(0));
      if (!bufferedContains(this.video, t)) return;
    }
    try {
      this.video.currentTime = t;
    } catch {
      /* seeking may throw transiently; next LEVEL_UPDATED retries */
    }
  };
}

const BUFFER_MARGIN_S = 0.5; // stay this far inside the buffered edge when correcting

/** End of the buffered range covering `t` (allowing a tiny gap), or null if none. */
function bufferedEndAhead(video: HTMLVideoElement, t: number): number | null {
  const b = video.buffered;
  for (let i = 0; i < b.length; i++) {
    if (t >= b.start(i) - BUFFER_MARGIN_S && t < b.end(i)) return b.end(i);
  }
  return null;
}

/** True if `t` falls inside any buffered range (safe to seek to without a stall). */
function bufferedContains(video: HTMLVideoElement, t: number): boolean {
  const b = video.buffered;
  for (let i = 0; i < b.length; i++) {
    if (t >= b.start(i) && t <= b.end(i)) return true;
  }
  return false;
}
